/**
 * German for the automation vocabularies, and nothing else.
 *
 * PRESENTATION ONLY. Every value here maps a backend string to a word a reader
 * understands. Nothing is computed, derived or decided — the status logic lives
 * in `AutomationStatusPill`, the counts are folded in the page, and the
 * statistics come from the server. A map that started answering a question
 * would be a second engine beside the backend's, free to disagree with it.
 *
 * AN UNKNOWN VALUE FALLS THROUGH TO ITSELF, exactly as `requirementLabels`
 * does. The backend owns these vocabularies and may grow them; showing the raw
 * value is honest about a word we cannot translate yet, where a friendly
 * catch-all ("Sonstiges", "Unbekannt") would assert something the frontend does
 * not know. A new run status would read `queued` on screen — odd, but true —
 * instead of being silently folded into a wrong German word.
 *
 * `runtime_mode` AND `enabled` ARE NOT THE SAME AXIS and their words are kept
 * apart on purpose. "Automatisch" describes how an automation is REACHED; "Aktiv"
 * describes whether it is switched on. A scheduled automation that is switched
 * off is "Automatisch" and "Aus" at once, and one word doing both jobs would
 * make that state unsayable.
 */

/**
 * `category` — the registry's grouping.
 *
 * `tenant_data` arrived with the move-in address adoption, the first automation
 * that changes a record rather than sending a message. It is listed rather than
 * left to the fall-through above for a specific reason: that fall-through is
 * for values the frontend does not YET know, and showing `tenant_data` raw on
 * screen would be exactly the untranslated word it exists to admit to. A
 * vocabulary we ship in the same change is one we know.
 */
export const AUTOMATION_CATEGORY_LABELS = {
  email: "E-Mails",
  tenant_data: "Mieterdaten",
};

/** `trigger_type` — what starts the automation. */
export const AUTOMATION_TRIGGER_LABELS = {
  date: "Datum",
  manual: "Manuell",
  schedule: "Zeitplan",
  test: "Test",
};

/** `runtime_mode` — how the automation is reached. NOT its on/off state. */
export const AUTOMATION_RUNTIME_MODE_LABELS = {
  scheduled: "Automatisch",
  manual_only: "Manuell",
  // PC4: registered, template preparable, nothing runs it yet.
  preparing: "In Vorbereitung",
};

/**
 * `action_type` — what the automation does once it is triggered (D9, Auslöser).
 *
 * The two values the registry declares today, listed for the same reason as
 * `tenant_data` above: a vocabulary we ship is one we know.
 */
export const AUTOMATION_ACTION_LABELS = {
  send_email: "E-Mail senden",
  update_tenant_address: "Wohnadresse übernehmen",
};

/**
 * `action` of one Protokoll entry — the audit actions written into the
 * automation's own stream (D9).
 *
 * Six today: A5a.2's switch writes `automation_enabled`/`automation_disabled`,
 * D4's template history writes `automation_template_version_created`, and the
 * test send (TEST-EMAIL T2) writes one of three outcomes. Anything else falls
 * through to its raw value, as everywhere in this file — shown as what it is
 * rather than folded into a German word that might be wrong. The test send's
 * quota reservation is not an activity of the automation and never reaches
 * this stream; it has no label here on purpose.
 */
export const AUTOMATION_ACTIVITY_LABELS = {
  // Short verbs, not the status card's headlines: "Automatisierung
  // ausgeschaltet" is what the card says about the state NOW, and the same
  // sentence in a history row would read as a second statement of it.
  automation_enabled: "Eingeschaltet",
  automation_disabled: "Ausgeschaltet",
  automation_template_version_created: "Neue Version der E-Mail-Vorlage",
  // TEST-EMAIL T3. "Fehlgeschlagen" only where the provider refused it; an
  // outcome nobody could establish is "Status unklar", never a failure — the
  // same rule as a run's `indeterminate` below.
  automation_test_email_sent: "Test-E-Mail gesendet",
  automation_test_email_failed: "Test-E-Mail fehlgeschlagen",
  automation_test_email_indeterminate: "Test-E-Mail: Status unklar",
};

/**
 * `status` of a single run.
 *
 * `indeterminate` IS "Unklar" SINCE D11b, and never "Fehlgeschlagen". It is the
 * run whose outcome nobody could establish — a provider that did not answer, a
 * local write lost after the send. Calling it a failure would invite a second
 * send of a message that may well have arrived; calling it a success would hide
 * that nobody knows. The word says exactly that much and no more. Until D11b it
 * fell through raw (D8-U, E27).
 */
export const AUTOMATION_RUN_STATUS_LABELS = {
  running: "Läuft",
  succeeded: "Erfolgreich",
  failed: "Fehlgeschlagen",
  skipped: "Übersprungen",
  indeterminate: "Unklar",
};

/**
 * Badge tone per run status. The TEXT carries the meaning; the tone reinforces
 * it — never the other way round, so a reader who cannot distinguish the
 * colours loses nothing (§36).
 *
 * `skipped` is quiet rather than warning: a skipped run is usually the correct
 * outcome (nobody had a birthday today), not a problem to look at.
 */
export const AUTOMATION_RUN_STATUS_TONES = {
  running: "info",
  succeeded: "positive",
  failed: "bad",
  skipped: "muted",
  // Amber, not red: worth a look, not a failure (D11b).
  indeterminate: "warning",
};

export function automationCategoryLabel(value) {
  return AUTOMATION_CATEGORY_LABELS[value] || String(value ?? "");
}

export function automationTriggerLabel(value) {
  return AUTOMATION_TRIGGER_LABELS[value] || String(value ?? "");
}

export function automationRuntimeModeLabel(value) {
  return AUTOMATION_RUNTIME_MODE_LABELS[value] || String(value ?? "");
}

export function automationActionLabel(value) {
  return AUTOMATION_ACTION_LABELS[value] || String(value ?? "");
}

export function automationActivityLabel(value) {
  return AUTOMATION_ACTIVITY_LABELS[value] || String(value ?? "");
}

export function automationRunStatusLabel(value) {
  return AUTOMATION_RUN_STATUS_LABELS[value] || String(value ?? "");
}

export function automationRunStatusTone(value) {
  return AUTOMATION_RUN_STATUS_TONES[value] || "neutral";
}

/**
 * Tailwind classes per tone, written LITERALLY so the JIT sees the full
 * strings — never built as `bg-${tone}-50`, which gets purged. Light and dark
 * are paired on every tone, matching `RequirementsTable`'s map.
 */
export const AUTOMATION_TONE_CLASSES = {
  positive:
    "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
  bad: "border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
  info: "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300",
  // D11b — the outcome of an `indeterminate` run: not good, not bad, unknown.
  warning:
    "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  muted:
    "border-border bg-muted text-muted-foreground dark:border-white/10 dark:bg-white/[0.04] dark:text-[#8b9ab5]",
  neutral:
    "border-border bg-card text-foreground dark:border-white/10 dark:bg-white/[0.03]",
  // `primary` IS NOT A STATUS TONE, it is the brand. It marks the active,
  // interactive Vantio surface — the switched-on automation, the selected row,
  // the chosen tab — and it is deliberately NOT `positive`: green on this page
  // means one thing only, "this went well", and it belongs to run outcomes.
  // An automation being switched on is not a success, it is a state.
  primary:
    "border-primary/30 bg-primary/[0.07] text-foreground dark:border-primary/40 dark:bg-primary/[0.14]",
};

export function automationToneClass(tone) {
  return AUTOMATION_TONE_CLASSES[tone] || AUTOMATION_TONE_CLASSES.neutral;
}

/**
 * The same tones as a filled icon tile — no border, a stronger tint.
 *
 * KEPT BESIDE THE BADGE MAP ON PURPOSE. A tone is a meaning ("this went well",
 * "this is quiet"), and the redesign renders that meaning in two shapes: a
 * bordered pill around a word and a tinted square around a glyph. Two maps in
 * two files would let "positive" drift into two different greens, which is how
 * a page stops looking like one page.
 *
 * `neutral` IS THE BRAND BLUE, not a grey. It marks the ordinary, primary
 * object of this page — the automation itself — which is why the category tile
 * in every row carries it. Grey is `muted`, and it means "quiet, nothing to
 * see here", which an automation's own identity is not.
 */
export const AUTOMATION_ICON_TONE_CLASSES = {
  positive:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  bad: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  info: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  warning: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  // No dark `text-[#…]` override here, unlike the badge map above: that hex
  // predates the token and `--muted-foreground` resolves to the same grey-blue
  // in dark. A new hardcoded colour is a value that stops following the theme
  // the day the theme moves.
  muted: "bg-muted text-muted-foreground dark:bg-white/[0.06]",
  neutral: "bg-primary/10 text-primary dark:bg-primary/20",
  // A shade stronger than `neutral`, because it sits ON a primary-tinted card
  // and a tile the same weight as its own background is not a tile.
  primary: "bg-primary/[0.18] text-primary dark:bg-primary/30",
};

export function automationIconToneClass(tone) {
  return (
    AUTOMATION_ICON_TONE_CLASSES[tone] || AUTOMATION_ICON_TONE_CLASSES.neutral
  );
}
